interface ProgressStats {
    totalItems: number
    itemsProcessed: number
    wordsProcessed: number
    completedBatches: number
    totalBatches: number
}

/**
 * processItemsInWorkerPool()
 *
 * @param items - The full array of items to be processed (could be 100,000+).
 * @param workerUrl - The URL or module path for your Worker script.
 * @param poolSize - Number of workers in the pool.
 * @param chunkSize - How many items per batch (defaults to 1000).
 * @returns A Promise resolving to an array of processed items, in the same order as the input.
 */
export async function processItemsInWorkerPool(
    items: string[],
    poolSize: number,
    chunkSize = 1000,
    progressReporter?: (stats: ProgressStats) => void
): Promise<string[]> {
    console.debug(`[processItemsInWorkerPool] Starting with ${items.length} items, chunkSize=${chunkSize}`)

    const batches: string[][] = []
    for (let i = 0; i < items.length; i += chunkSize) {
        batches.push(items.slice(i, i + chunkSize))
    }
    console.debug(`[processItemsInWorkerPool] Created ${batches.length} batches`)

    const resultArray = new Array<string>(items.length)
    const workers: Worker[] = Array.from(
        { length: poolSize },
        () => new Worker(new URL("./worker", import.meta.url), { type: "module" })
    )
    const idleWorkers = [...workers]

    type Task = {
        batchIndex: number
        batchItems: string[]
        startIndex: number
        endIndex: number
    }
    const tasks: Task[] = batches.map((batchItems, batchIndex) => {
        const startIndex = batchIndex * chunkSize
        const endIndex = Math.min(startIndex + chunkSize, items.length)
        return { batchIndex, batchItems, startIndex, endIndex }
    })
    console.debug(`[processItemsInWorkerPool] Created ${tasks.length} tasks for workers`)

    let completedBatches = 0
    let resolveAll: (res: string[]) => void
    let rejectAll: (err: Error) => void
    const allDonePromise = new Promise<string[]>((resolve, reject) => {
        resolveAll = resolve
        rejectAll = reject
    })

    function scheduleTasks() {
        console.debug(`[processItemsInWorkerPool] Scheduling tasks. Idle workers: ${idleWorkers.length}`)
        while (idleWorkers.length > 0 && tasks.length > 0) {
            const worker = idleWorkers.shift()!
            const task = tasks.shift()!
            console.debug(`[processItemsInWorkerPool] Assigning batch #${task.batchIndex} to worker`)

            worker.postMessage({
                batchIndex: task.batchIndex,
                startIndex: task.startIndex,
                endIndex: task.endIndex,
                items: task.batchItems,
            })
        }
    }

    function handleWorkerMessage(e: MessageEvent) {
        const data = e.data

        if (data.status === "complete") {
            const { batchIndex, startIndex, endIndex, processedItems } = data
            console.debug(`[processItemsInWorkerPool] Worker completed batch #${batchIndex}`)

            for (let i = startIndex; i < endIndex; i++) {
                resultArray[i] = processedItems[i - startIndex]
            }

            completedBatches += 1

            if (progressReporter) {
                progressReporter({
                    totalItems: items.length,
                    itemsProcessed: endIndex,
                    totalBatches: batches.length,
                    completedBatches,
                    wordsProcessed: resultArray.reduce((acc, item) => acc + item.split(/\s+/).length, 0),
                })
            }

            if (completedBatches === batches.length) {
                console.debug(`[processItemsInWorkerPool] All batches completed`)
                cleanup()
                resolveAll!(resultArray)
            }
            const worker = e.target as Worker
            idleWorkers.push(worker)
            scheduleTasks()
        } else if (data.status === "error") {
            console.debug(`[processItemsInWorkerPool] Worker error: ${data.error}`)
            const worker = e.target as Worker
            cleanup()
            rejectAll!(new Error(data.error || "Worker error"))
        }
    }

    workers.forEach((worker) => {
        worker.addEventListener("message", handleWorkerMessage)
        // worker.addEventListener("error", (err) => {
        //     console.debug(`[processItemsInWorkerPool] Worker runtime error:`, err)
        //     cleanup()
        //     rejectAll!(err)
        // })
    })

    scheduleTasks()
    if (batches.length === 0) {
        console.debug(`[processItemsInWorkerPool] No batches to process, resolving immediately`)
        resolveAll!(resultArray)
    }

    function cleanup() {
        console.debug(`[processItemsInWorkerPool] Cleaning up workers`)
        workers.forEach((worker) => {
            worker.removeEventListener("message", handleWorkerMessage)
            worker.terminate()
        })
    }

    return allDonePromise
}
